import React, { createContext, type FC, type ReactNode, use, useCallback, useMemo } from "react";
import { useToastSaver } from "@ui-components/hooks/useToastSaver";
import { child, type DataSnapshot } from "firebase/database";
import { refCashRegisters } from "@domain/api/firebase/firebaseRootRefs";

import { CompanyContext } from "@domain/context/CompanyContext";
import { useRecordsKB } from "@domain/context/accountingData/RecordsCtx";
import { type GenericRecord } from "@domain/models";
import { type GQL } from "@binale-tech/shared";
import { useMutation } from "@apollo/client/react";
import {
    cashRegisterAccountingBind,
    cashRegisterDelete,
    cashRegisterMarkFavorite,
    cashRegisterSave,
    cashRegisterTransactionSave,
    cashRegisterTransactionsColor,
    cashRegisterTransactionsDelete,
    cashRegisterTransactionSetDmsDocumentIds,
} from "@domain/context/mutations/cashRegisterMutations.graphql";
import { useCollectionListener } from "@domain/context/hooks/useCollectionListener";
import { useGroupedCollectionListener } from "@domain/context/hooks/useGroupedCollectionListener";
import { type TCashRegisterTransactionData } from "@cash-register/types";

export interface ICashRegisterController {
    saveCashRegister: (input: Omit<GQL.ICashRegisterSaveInput, "companyId">) => Promise<GQL.ICashRegister>;
    deleteCashRegister: (id: string) => Promise<void>;
    markFavoriteCashRegister: (id: string) => Promise<void>;
    bindToAccounting: (registerId: string, kbId: string) => Promise<void>;
    saveTransaction: (cashRegisterId: string, input: GQL.ICashRegisterTransactionSaveInput) => Promise<boolean>;
    deleteTransactions: (cashRegisterId: string, ids: string[]) => Promise<void>;
    setTransactionDocumentIds: (cashRegisterId: string, transactionId: string, documents: string[]) => Promise<void>;
    setTransactionsColor: (cashRegisterId: string, ids: string[], color: GQL.IDocumentColors | null) => Promise<void>;
}

type TRegistersValue = {
    registers: GQL.ICashRegister[];
    registersById: Map<string, GQL.ICashRegister>;
};

type TTransactionsValue = {
    transactionsByRegister: Map<string, Map<string, GQL.ICashRegisterTransaction>>;
    transactionsData: Record<string, TCashRegisterTransactionData>;
    transactionMap: Map<string, GQL.ICashRegisterTransaction>;
    transactionRecordsMap: Map<string, GenericRecord>;
};

const EMPTY_INNER_MAP: ReadonlyMap<string, GQL.ICashRegisterTransaction> = new Map();

export const CashRegisterContext = createContext<TRegistersValue>({
    registers: [],
    registersById: new Map(),
});
export const CashRegisterTransactionsContext = createContext<TTransactionsValue>({
    transactionsByRegister: new Map(),
    transactionsData: {},
    transactionMap: new Map(),
    transactionRecordsMap: new Map(),
});
export const CashRegisterControlContext = createContext<ICashRegisterController>({} as ICashRegisterController);

export const useCashRegisterTransactions = (registerId: string | undefined) => {
    const { transactionsByRegister } = use(CashRegisterTransactionsContext);
    const innerMap = registerId ? transactionsByRegister.get(registerId) : undefined;
    return useMemo(() => {
        const list: GQL.ICashRegisterTransaction[] = [];
        if (innerMap) {
            for (const t of innerMap.values()) {
                list.push(t);
            }
        }
        return {
            list,
            byId: innerMap ?? EMPTY_INNER_MAP,
        };
    }, [innerMap]);
};

type TProps = {
    children: ReactNode;
};

export const CashRegisterContextProvider: FC<TProps> = ({ children }) => {
    const { companyGQL } = use(CompanyContext);
    const companyId = companyGQL?.id;
    const recordsKB = useRecordsKB();
    const toastSaver = useToastSaver();

    const [saveMutation] = useMutation(cashRegisterSave);
    const [deleteMutation] = useMutation(cashRegisterDelete);
    const [markFavoriteMutation] = useMutation(cashRegisterMarkFavorite);
    const [accountingBindMutation] = useMutation(cashRegisterAccountingBind);
    const [transactionSaveMutation] = useMutation(cashRegisterTransactionSave);
    const [transactionsDeleteMutation] = useMutation(cashRegisterTransactionsDelete);
    const [transactionSetDmsDocumentIdsMutation] = useMutation(cashRegisterTransactionSetDmsDocumentIds);
    const [transactionsColorMutation] = useMutation(cashRegisterTransactionsColor);

    const ref = useMemo(() => child(refCashRegisters, `${companyId}/registers`), [companyId]);
    const initializer = useCallback((snap: DataSnapshot): GQL.ICashRegister => snap.val(), []);
    const shouldSkipLoad = !companyId;

    const { list: registers } = useCollectionListener(ref, initializer, shouldSkipLoad);

    const transactionsBaseRef = useMemo(() => child(refCashRegisters, `${companyId}/transactions`), [companyId]);
    const registerIds = useMemo(() => registers.map((r) => ({ groupId: r.id, subPath: r.id })), [registers]);
    const transactionInitializer = useCallback((snap: DataSnapshot): GQL.ICashRegisterTransaction => snap.val(), []);
    const { state: transactionsByRegister } = useGroupedCollectionListener(
        transactionsBaseRef,
        registerIds,
        transactionInitializer,
        shouldSkipLoad,
    );

    const registersById = useMemo(() => {
        const map = new Map<string, GQL.ICashRegister>();
        for (const r of registers) {
            map.set(r.id, r);
        }
        return map;
    }, [registers]);

    const transactionsData = useMemo<Record<string, TCashRegisterTransactionData>>(() => {
        const result: Record<string, TCashRegisterTransactionData> = {};
        transactionsByRegister.forEach((group, registerId) => {
            result[registerId] = Object.fromEntries(group);
        });
        return result;
    }, [transactionsByRegister]);

    const transactionMap = useMemo(() => {
        const map = new Map<string, GQL.ICashRegisterTransaction>();
        for (const innerMap of transactionsByRegister.values()) {
            for (const t of innerMap.values()) {
                map.set(t.id, t);
            }
        }
        return map;
    }, [transactionsByRegister]);

    const transactionRecordsMap = useMemo(() => {
        const map = new Map<string, GenericRecord>();
        recordsKB.combinedList.forEach((r) => {
            if (r.cashRegisterTransactionId) {
                map.set(r.cashRegisterTransactionId, r);
            }
        });
        return map;
    }, [recordsKB]);

    const registersValue: TRegistersValue = useMemo(() => ({ registers, registersById }), [registers, registersById]);

    const transactionsValue: TTransactionsValue = useMemo(
        () => ({ transactionsByRegister, transactionsData, transactionMap, transactionRecordsMap }),
        [transactionsByRegister, transactionsData, transactionMap, transactionRecordsMap],
    );

    const actions: ICashRegisterController = useMemo(
        () =>
            ({
                saveCashRegister: async (input) => {
                    let result: GQL.ICashRegister;
                    await toastSaver(async () => {
                        const res = await saveMutation({
                            variables: { input: { ...input, companyId } },
                        });
                        result = res.data?.cashRegisterSave;
                    });
                    return result;
                },
                deleteCashRegister: async (id) => {
                    await toastSaver(() =>
                        deleteMutation({
                            variables: { input: { id, companyId } },
                        }),
                    );
                },
                markFavoriteCashRegister: async (id) => {
                    await toastSaver(() =>
                        markFavoriteMutation({
                            variables: { input: { id, companyId } },
                        }),
                    );
                },
                bindToAccounting: async (registerId, kbId) => {
                    await toastSaver(() =>
                        accountingBindMutation({
                            variables: {
                                input: {
                                    companyId,
                                    sourceId: registerId,
                                    accountingTargetId: { kbId: kbId || undefined },
                                },
                            },
                        }),
                    );
                },
                saveTransaction: async (cashRegisterId, input) => {
                    const result = await toastSaver(() =>
                        transactionSaveMutation({
                            variables: { companyId, cashRegisterId, input },
                        }),
                    );
                    return result !== undefined;
                },
                deleteTransactions: async (cashRegisterId, ids) => {
                    await toastSaver(() =>
                        transactionsDeleteMutation({
                            variables: { ids: { ids, companyId }, cashRegisterId },
                        }),
                    );
                },
                setTransactionDocumentIds: async (cashRegisterId, transactionId, documents) => {
                    await toastSaver(() =>
                        transactionSetDmsDocumentIdsMutation({
                            variables: {
                                id: { id: transactionId, companyId },
                                cashRegisterId,
                                documents: documents.map((id) => ({ id })),
                            },
                        }),
                    );
                },
                setTransactionsColor: async (cashRegisterId, ids, color) => {
                    await toastSaver(() =>
                        transactionsColorMutation({
                            variables: { ids: { ids, companyId }, cashRegisterId, color },
                        }),
                    );
                },
            }) satisfies ICashRegisterController,
        [
            saveMutation,
            deleteMutation,
            markFavoriteMutation,
            accountingBindMutation,
            transactionSaveMutation,
            transactionsDeleteMutation,
            transactionSetDmsDocumentIdsMutation,
            transactionsColorMutation,
            companyId,
            toastSaver,
        ],
    );

    return (
        <CashRegisterContext value={registersValue}>
            <CashRegisterTransactionsContext value={transactionsValue}>
                <CashRegisterControlContext value={actions}>{children}</CashRegisterControlContext>
            </CashRegisterTransactionsContext>
        </CashRegisterContext>
    );
};
